import type { Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import type {
  ChecklistSubmission,
  ChecklistRunDetail,
  ChecklistRunFilters,
  ChecklistRunSummary,
  CreateSubmissionInput,
  CreateSubmissionResponseInput,
  FieldSnapshot,
  SubmissionResponse,
} from './types';

type ChecklistRunRow = Omit<ChecklistSubmission, 'checklist_id'> & {
  template_id: string;
};

type ChecklistRunRowWithChecklist = ChecklistRunRow & {
  checklist: { id: string; name: string };
};

type ChecklistRunItemResponseRow = Omit<SubmissionResponse, 'submission_id' | 'item_id'> & {
  run_id: string;
  template_item_id: string;
};

function mapSubmissionRow(row: ChecklistRunRow): ChecklistSubmission {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    checklist_id: row.template_id,
    submitted_by: row.submitted_by,
    status: row.status,
    notes: row.notes,
    shift_date: row.shift_date,
    completed_at: row.completed_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function mapSubmissionResponseRow(row: ChecklistRunItemResponseRow): SubmissionResponse {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    submission_id: row.run_id,
    item_id: row.template_item_id,
    response_value: row.response_value,
    photo_url: row.photo_url,
    is_passed: row.is_passed,
    response_payload: row.response_payload,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function buildResponsePayload(response: CreateSubmissionResponseInput): Json {
  if (response.response_payload) {
    return response.response_payload;
  }

  return {
    response_value: response.response_value ?? null,
    photo_url: response.photo_url ?? null,
    is_passed: response.is_passed ?? null,
  } as Json;
}

export async function fetchUserRecentSubmissions(
  tenantId: string,
  userId: string,
  limit = 5,
): Promise<Array<ChecklistSubmission & { checklist: { id: string; name: string } }>> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_runs')
    .select(
      `
      *,
      checklist:mod_checklist_templates(id, name)
    `,
    )
    .eq('tenant_id', tenantId)
    .eq('submitted_by', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return ((data ?? []) as ChecklistRunRowWithChecklist[]).map((row) => ({
    ...mapSubmissionRow(row),
    checklist: row.checklist,
  }));
}

export async function createChecklistSubmission(
  input: CreateSubmissionInput,
): Promise<ChecklistSubmission> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_runs')
    .insert({
      tenant_id: input.tenant_id,
      template_id: input.checklist_id,
      submitted_by: input.submitted_by,
      status: input.status,
      notes: input.notes ?? null,
      shift_date: input.shift_date,
      completed_at: input.completed_at ?? null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return mapSubmissionRow(data as ChecklistRunRow);
}

export async function createSubmissionResponses(
  responses: CreateSubmissionResponseInput[],
): Promise<void> {
  if (responses.length === 0) return;

  const rows = responses.map((response) => ({
    tenant_id: response.tenant_id,
    run_id: response.submission_id,
    template_item_id: response.item_id,
    response_value: response.response_value ?? null,
    photo_url: response.photo_url ?? null,
    is_passed: response.is_passed ?? null,
    response_payload: buildResponsePayload(response),
  }));

  const { data: insertedRows, error } = await supabase
    .schema('core')
    .from('mod_checklist_run_item_responses')
    .insert(rows)
    .select('id, template_item_id');

  if (error) throw error;

  // Write field snapshots for responses that have them
  const snapshotRows: Array<{
    tenant_id: string;
    run_id: string;
    run_item_response_id: string;
    template_item_id: string;
    captured_value: string | null;
    capture_status: string;
    metadata: Record<string, unknown>;
  }> = [];

  const insertedMap = new Map(
    (insertedRows ?? []).map((r) => [r.template_item_id as string, r.id as string]),
  );

  for (const response of responses) {
    if (!response.field_snapshots || response.field_snapshots.length === 0) continue;
    const responseId = insertedMap.get(response.item_id);
    if (!responseId) continue;

    for (const snap of response.field_snapshots) {
      snapshotRows.push({
        tenant_id: response.tenant_id,
        run_id: response.submission_id,
        run_item_response_id: responseId,
        template_item_id: response.item_id,
        captured_value: snap.value,
        capture_status: snap.capture_status ?? 'ok',
        metadata: {
          mapping_id: snap.mapping_id,
          display_name: snap.display_name,
          unit: snap.unit,
        },
      });
    }
  }

  if (snapshotRows.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: snapError } = await (supabase.schema('core') as any)
      .from('mod_checklist_run_field_snapshots')
      .insert(snapshotRows);

    if (snapError) {
      console.error('Failed to write field snapshots:', snapError);
      // Non-fatal: submission still succeeded
    }
  }
}

export async function fetchSubmissionResponses(
  tenantId: string,
  submissionId: string,
): Promise<SubmissionResponse[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.schema('core') as any)
    .from('mod_checklist_run_item_responses')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('run_id', submissionId)
    .order('created_at');

  if (error) throw error;
  return ((data ?? []) as ChecklistRunItemResponseRow[]).map(mapSubmissionResponseRow);
}

export async function fetchRunsByFactory(
  tenantId: string,
  factoryId: string,
  filters?: ChecklistRunFilters,
): Promise<ChecklistRunSummary[]> {
  // Resolve template IDs for this factory
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: templates, error: tErr } = await (supabase.schema('core') as any)
    .from('mod_checklist_templates')
    .select('id')
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId);

  if (tErr) throw tErr;
  const templateIds = ((templates ?? []) as { id: string }[]).map((t) => t.id);
  if (templateIds.length === 0) return [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query: any = (supabase.schema('core') as any)
    .from('mod_checklist_runs')
    .select('*, template:mod_checklist_templates(id, name)')
    .eq('tenant_id', tenantId)
    .in('template_id', templateIds)
    .order('created_at', { ascending: false });

  if (filters?.status) query = query.eq('status', filters.status);
  if (filters?.templateId) query = query.eq('template_id', filters.templateId);
  if (filters?.operatorId) query = query.eq('submitted_by', filters.operatorId);
  if (filters?.dateFrom) query = query.gte('shift_date', filters.dateFrom);
  if (filters?.dateTo) query = query.lte('shift_date', filters.dateTo);

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []) as Array<ChecklistRunRow & { template: { id: string; name: string } | null }>;

  // Batch-load operator profiles
  const operatorIds = [...new Set(rows.map((r) => r.submitted_by))];
  let profilesMap: Record<string, { full_name: string | null; email: string }> = {};
  if (operatorIds.length > 0) {
    const { data: profiles } = await supabase
      .schema('core')
      .from('profiles')
      .select('id, full_name, email')
      .in('id', operatorIds);
    if (profiles) {
      profilesMap = Object.fromEntries(
        profiles.map((p) => [p.id, { full_name: p.full_name, email: p.email }]),
      );
    }
  }

  return rows.map((row) => ({
    ...mapSubmissionRow(row),
    template: row.template ?? null,
    operator: profilesMap[row.submitted_by]
      ? { id: row.submitted_by, ...profilesMap[row.submitted_by] }
      : null,
  }));
}

export async function fetchRunWithResponses(
  tenantId: string,
  runId: string,
): Promise<ChecklistRunDetail | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: run, error: runErr } = await (supabase.schema('core') as any)
    .from('mod_checklist_runs')
    .select('*, template:mod_checklist_templates(id, name)')
    .eq('tenant_id', tenantId)
    .eq('id', runId)
    .single();

  if (runErr) throw runErr;
  if (!run) return null;

  const typedRun = run as ChecklistRunRow & { template: { id: string; name: string } | null };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: responses, error: resErr } = await (supabase.schema('core') as any)
    .from('mod_checklist_run_item_responses')
    .select('*, item:mod_checklist_template_items(id, label, item_type, sort_order, label_translations)')
    .eq('tenant_id', tenantId)
    .eq('run_id', runId)
    .order('created_at');

  if (resErr) throw resErr;

  const { data: profile } = await supabase
    .schema('core')
    .from('profiles')
    .select('id, full_name, email')
    .eq('id', typedRun.submitted_by)
    .maybeSingle();

  const mappedResponses = ((responses ?? []) as Array<ChecklistRunItemResponseRow & { item: { id: string; label: string; item_type: string; sort_order: number; label_translations: Json | null } | null }>).map(
    (r) => ({
      ...mapSubmissionResponseRow(r),
      item: r.item ?? null,
    }),
  );

  // Fetch field snapshots for this run
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: snapshots } = await (supabase.schema('core') as any)
    .from('mod_checklist_run_field_snapshots')
    .select('id, run_item_response_id, captured_value, capture_status, metadata, captured_at')
    .eq('tenant_id', tenantId)
    .eq('run_id', runId);

  // Group snapshots by response id
  const snapshotsByResponse = new Map<string, FieldSnapshot[]>();
  for (const snap of (snapshots ?? []) as Array<{ id: string; run_item_response_id: string; captured_value: string | null; capture_status: string; metadata: Json; captured_at: string }>) {
    const list = snapshotsByResponse.get(snap.run_item_response_id) ?? [];
    list.push({
      id: snap.id,
      captured_value: snap.captured_value,
      capture_status: snap.capture_status,
      metadata: snap.metadata,
      captured_at: snap.captured_at,
    });
    snapshotsByResponse.set(snap.run_item_response_id, list);
  }

  const responsesWithSnapshots = mappedResponses.map((r) => ({
    ...r,
    field_snapshots: snapshotsByResponse.get(r.id) ?? [],
  }));

  return {
    ...mapSubmissionRow(typedRun),
    template: typedRun.template ?? null,
    operator: profile
      ? { id: profile.id, full_name: profile.full_name, email: profile.email }
      : null,
    responses: responsesWithSnapshots,
  };
}
